import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import DashboardLayout from '../components/layout/DashboardLayout';
import DailyGoalWidget from '../components/DailyGoalWidget';
import StreakWidget from '../components/StreakWidget';
import GlassCard from '../components/ui/GlassCard';
import TrialCreditsSection from '../components/TrialCreditsSection';

const Dashboard = () => {
    const { user, getToken, userProfile } = useAuth();
    const navigate = useNavigate();
    const [checkingOnboarding, setCheckingOnboarding] = useState(true);

    // Check onboarding status on mount
    useEffect(() => {
        async function checkOnboarding() {
            try {
                const token = getToken();
                if (!token) {
                    setCheckingOnboarding(false);
                    return;
                }

                const data = await api.get('/api/onboarding/status', {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!data.completed) {
                    // Redirect to onboarding if not completed
                    navigate('/onboarding', { replace: true });
                    return;
                }
            } catch (error) {
                console.error('Error checking onboarding:', error);
                // On error, redirect to onboarding to be safe
                navigate('/onboarding', { replace: true });
                return;
            }
            setCheckingOnboarding(false);
        }

        checkOnboarding();
    }, [getToken, navigate]);

    // Show loading while checking onboarding
    if (checkingOnboarding) {
        return (
            <DashboardLayout>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh',
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        border: '3px solid var(--border-subtle)',
                        borderTop: '3px solid var(--indigo-500)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                    }} />
                </div>
            </DashboardLayout>
        );
    }

    const getDisplayName = () => {
        // First check userProfile (from onboarding), then OAuth metadata, then email
        // Use first name only for greeting
        if (userProfile?.full_name) {
            return userProfile.full_name.split(' ')[0];
        }
        if (user?.user_metadata?.full_name) {
            return user.user_metadata.full_name.split(' ')[0];
        }
        const email = user?.email || '';
        return email.split('@')[0];
    };

    const quickActions = [
        {
            id: 'discover',
            title: 'Discover Jobs',
            description: 'Browse opportunities matching your skills',
            icon: (
                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                    <circle cx="20" cy="20" r="14" stroke="url(#discover-icon)" strokeWidth="3"/>
                    <path d="M30 30l12 12" stroke="url(#discover-icon)" strokeWidth="3" strokeLinecap="round"/>
                    <defs>
                        <linearGradient id="discover-icon" x1="6" y1="6" x2="42" y2="42">
                            <stop offset="0%" stopColor="#6366F1"/>
                            <stop offset="100%" stopColor="#22D3EE"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            path: '/discover-jobs',
            variant: 'discover',
        },
        {
            id: 'resume',
            title: 'Generate Resumes',
            description: 'Create tailored resumes in seconds',
            icon: (
                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                    <rect x="8" y="4" width="24" height="32" rx="2" stroke="url(#resume-icon)" strokeWidth="3"/>
                    <path d="M14 12h12M14 18h12M14 24h8" stroke="url(#resume-icon)" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="34" cy="34" r="10" fill="url(#resume-icon-fill)"/>
                    <path d="M31 34l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                        <linearGradient id="resume-icon" x1="8" y1="4" x2="40" y2="44">
                            <stop offset="0%" stopColor="#22D3EE"/>
                            <stop offset="100%" stopColor="#6366F1"/>
                        </linearGradient>
                        <linearGradient id="resume-icon-fill" x1="24" y1="24" x2="44" y2="44">
                            <stop offset="0%" stopColor="#6366F1"/>
                            <stop offset="100%" stopColor="#22D3EE"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            path: '/generate-resumes',
            variant: 'resume',
        },
        {
            id: 'interview',
            title: 'Interview Prep',
            description: 'AI-powered practice and feedback',
            icon: (
                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                    <path d="M40 22c0-8.837-7.163-16-16-16S8 13.163 8 22c0 5.105 2.392 9.647 6.118 12.583L12 44l6.5-4 6.5 4 6.5-4 6.5 4-2.118-9.417C39.608 31.647 40 27.105 40 22z" stroke="url(#interview-icon)" strokeWidth="3"/>
                    <circle cx="18" cy="20" r="2" fill="url(#interview-icon)"/>
                    <circle cx="30" cy="20" r="2" fill="url(#interview-icon)"/>
                    <path d="M20 27c1.5 2 4.5 2 6 0" stroke="url(#interview-icon)" strokeWidth="2" strokeLinecap="round"/>
                    <defs>
                        <linearGradient id="interview-icon" x1="8" y1="6" x2="40" y2="44">
                            <stop offset="0%" stopColor="#F59E0B"/>
                            <stop offset="100%" stopColor="#6366F1"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            path: '/interview-prep',
            variant: 'interview',
        },
        {
            id: 'quiz',
            title: 'Quiz Practice',
            description: 'Study, Quiz, and Daily challenge modes',
            icon: (
                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                    <path d="M24 4l4 8 8 1-6 5.5 1.5 8.5-7.5-4-7.5 4 1.5-8.5-6-5.5 8-1 4-8z" stroke="url(#quiz-icon)" strokeWidth="3" strokeLinejoin="round"/>
                    <circle cx="24" cy="36" r="8" stroke="url(#quiz-icon)" strokeWidth="3"/>
                    <path d="M21 36l2 2 4-4" stroke="url(#quiz-icon)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                        <linearGradient id="quiz-icon" x1="10" y1="4" x2="38" y2="44">
                            <stop offset="0%" stopColor="#22D3EE"/>
                            <stop offset="100%" stopColor="#F59E0B"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            path: '/quiz-practice',
            variant: 'quiz',
        },
        {
            id: 'progress',
            title: 'Track Progress',
            description: 'Monitor metrics and weekly performance',
            icon: (
                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                    <rect x="6" y="28" width="8" height="14" rx="2" fill="url(#progress-icon)" opacity="0.6"/>
                    <rect x="20" y="18" width="8" height="24" rx="2" fill="url(#progress-icon)" opacity="0.8"/>
                    <rect x="34" y="8" width="8" height="34" rx="2" fill="url(#progress-icon)"/>
                    <path d="M6 10l12 8 12-6 12 4" stroke="url(#progress-icon)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                        <linearGradient id="progress-icon" x1="6" y1="8" x2="42" y2="42">
                            <stop offset="0%" stopColor="#6366F1"/>
                            <stop offset="100%" stopColor="#22D3EE"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            path: '/progress',
            variant: 'progress',
        },
    ];

    const styles = {
        widgetsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
            gap: 'var(--space-xl)',
            marginBottom: 'var(--space-2xl)',
        },
        sectionTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-lg)',
        },
        quickActionsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
            gap: 'var(--space-lg)',
        },
        actionCard: {
            padding: 'var(--space-xl)',
            cursor: 'pointer',
        },
        actionIcon: {
            width: '48px',
            height: '48px',
            marginBottom: 'var(--space-md)',
        },
        actionTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-xs)',
        },
        actionDescription: {
            fontSize: '0.875rem',
            color: 'var(--text-tertiary)',
            lineHeight: 1.5,
        },
    };

    return (
        <DashboardLayout
            title={`Welcome back, ${getDisplayName()}`}
            subtitle="Your job search command center"
        >
            {/* Daily Goal and Streak Widgets */}
            <div style={styles.widgetsGrid}>
                <DailyGoalWidget />
                <StreakWidget />
            </div>

            {/* Quick Actions Section */}
            <h2 style={styles.sectionTitle}>Quick Actions</h2>

            <div style={styles.quickActionsGrid}>
                {quickActions.map((action) => (
                    <GlassCard
                        key={action.id}
                        variant={action.variant}
                        hoverable
                        onClick={() => navigate(action.path)}
                        style={styles.actionCard}
                    >
                        <div style={styles.actionIcon}>
                            {action.icon}
                        </div>
                        <h3 style={styles.actionTitle}>{action.title}</h3>
                        <p style={styles.actionDescription}>{action.description}</p>
                    </GlassCard>
                ))}
            </div>

            {/* Trial Credits Section - Shows for non-premium users */}
            <TrialCreditsSection />
        </DashboardLayout>
    );
};

export default Dashboard;
